import {
  Property,
  PropertyType,
  PropertyContactInfo,
  OperatingHours,
  PropertyRule,
  RuleCategory,
  RulePriority,
  Unit,
} from '../models/property.model';
import { Amenity, AmenityType } from '../models/booking.model';
import { PetPolicy, PetType } from '../models/pet-registry.model';

export const MOCK_PROPERTIES: Property[] = [
  {
    id: 1,
    name: 'Sunset Gardens Apartments',
    nameKey: 'properties.sunset-gardens-apartments',
    address: '123 Sunset Boulevard',
    addressKey: 'properties.sunset-boulevard-address',
    city: 'New York',
    state: 'NY',
    zipCode: '10001',
    country: 'USA',
    propertyType: PropertyType.APARTMENT,
    totalUnits: 120,
    occupiedUnits: 98,
    amenities: [
      {
        id: 1,
        name: 'Swimming Pool',
        description: 'Outdoor swimming pool with deck area',
        type: AmenityType.POOL,
        capacity: 20,
        hourlyRate: 0,
        currency: 'USD',
        isActive: true,
        location: 'Ground Floor - Outdoor',
        amenities: ['Pool', 'Deck', 'Lounge Chairs', 'Shower'],
      },
      {
        id: 2,
        name: 'Fitness Center',
        description: 'Fully equipped gym with cardio and weight training equipment',
        type: AmenityType.GYM,
        capacity: 15,
        hourlyRate: 0,
        currency: 'USD',
        isActive: true,
        location: 'First Floor - Indoor',
        amenities: ['Treadmills', 'Weights', 'Yoga Mats', 'Mirrors'],
      },
      {
        id: 3,
        name: 'Conference Room A',
        description: 'Professional meeting room with presentation equipment',
        type: AmenityType.CONFERENCE_ROOM,
        capacity: 12,
        hourlyRate: 25.0,
        currency: 'USD',
        isActive: true,
        location: 'Second Floor - Indoor',
        amenities: ['Projector', 'Whiteboard', 'Video Conferencing', 'Coffee Station'],
      },
    ],
    managementCompany: 'Sunset Property Management LLC',
    managementCompanyKey: 'properties.sunset-property-management',
    managerId: 1,
    isActive: true,
    createdDate: new Date('2020-01-15T00:00:00Z'),
    updatedDate: new Date('2024-01-10T14:30:00Z'),
    description: 'Modern apartment complex with excellent amenities and prime location',
    descriptionKey: 'properties.sunset-gardens-description',
    yearBuilt: 2018,
    parkingSpaces: 150,
    petPolicy: {
      id: 1,
      title: 'Pet Policy - Sunset Gardens',
      description: 'Pet-friendly community with specific guidelines',
      petType: PetType.DOG,
      maxPetsPerUnit: 2,
      weightLimit: 50,
      breedRestrictions: ['Pit Bull', 'Rottweiler'],
      requiredVaccinations: ['Rabies', 'DHPP', 'Bordetella'],
      registrationFee: 300,
      monthlyFee: 50,
      currency: 'USD',
      isActive: true,
      effectiveDate: new Date('2023-01-01T00:00:00Z'),
    },
    rules: [
      {
        id: 1,
        propertyId: 1,
        title: 'Quiet Hours',
        description: 'Residents must maintain quiet between 10 PM and 7 AM',
        category: RuleCategory.NOISE,
        isActive: true,
        effectiveDate: new Date('2023-01-01T00:00:00Z'),
        priority: RulePriority.HIGH,
      },
      {
        id: 2,
        propertyId: 1,
        title: 'Parking Regulations',
        description:
          'Each unit is assigned one parking space. Guest parking available for 24 hours maximum',
        category: RuleCategory.PARKING,
        isActive: true,
        effectiveDate: new Date('2023-01-01T00:00:00Z'),
        priority: RulePriority.MEDIUM,
      },
    ],
    contactInfo: {
      managementOffice: 'Sunset Gardens Management Office',
      emergencyPhone: '+1-555-9111',
      maintenancePhone: '+1-555-9112',
      email: 'management@sunsetgardens.com',
      website: 'https://www.sunsetgardens.com',
      officeHours: {
        monday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
        tuesday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
        wednesday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
        thursday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
        friday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
        saturday: { isOpen: true, openTime: '10:00', closeTime: '14:00' },
        sunday: { isOpen: false },
      },
    },
  },
  {
    id: 2,
    name: 'Riverside Condominiums',
    nameKey: 'properties.riverside-condominiums',
    address: '456 Riverside Drive',
    addressKey: 'properties.riverside-drive-address',
    city: 'New York',
    state: 'NY',
    zipCode: '10002',
    country: 'USA',
    propertyType: PropertyType.CONDOMINIUM,
    totalUnits: 80,
    occupiedUnits: 75,
    amenities: [
      {
        id: 4,
        name: 'Tennis Court',
        description: 'Professional tennis court with lighting',
        type: AmenityType.TENNIS_COURT,
        capacity: 4,
        hourlyRate: 15.0,
        currency: 'USD',
        isActive: true,
        location: 'Rooftop - Outdoor',
        amenities: ['Court', 'Net', 'Lighting', 'Seating'],
      },
    ],
    managementCompany: 'Riverside Property Group',
    managementCompanyKey: 'properties.riverside-property-group',
    managerId: 2,
    isActive: true,
    createdDate: new Date('2019-06-20T00:00:00Z'),
    updatedDate: new Date('2024-01-12T09:15:00Z'),
    description: 'Luxury condominium complex with river views',
    descriptionKey: 'properties.riverside-condominiums-description',
    yearBuilt: 2019,
    parkingSpaces: 100,
    petPolicy: {
      id: 2,
      title: 'Pet Policy - Riverside Condominiums',
      description: 'Strict pet policy with breed restrictions',
      petType: PetType.DOG,
      maxPetsPerUnit: 1,
      weightLimit: 25,
      breedRestrictions: ['Pit Bull', 'Rottweiler', 'German Shepherd'],
      requiredVaccinations: ['Rabies', 'DHPP'],
      registrationFee: 500,
      monthlyFee: 75,
      currency: 'USD',
      isActive: true,
      effectiveDate: new Date('2023-01-01T00:00:00Z'),
    },
    rules: [],
    contactInfo: {
      managementOffice: 'Riverside Management Office',
      emergencyPhone: '+1-555-9222',
      maintenancePhone: '+1-555-9223',
      email: 'info@riversidecondos.com',
      website: 'https://www.riversidecondos.com',
      officeHours: {
        monday: { isOpen: true, openTime: '08:00', closeTime: '18:00' },
        tuesday: { isOpen: true, openTime: '08:00', closeTime: '18:00' },
        wednesday: { isOpen: true, openTime: '08:00', closeTime: '18:00' },
        thursday: { isOpen: true, openTime: '08:00', closeTime: '18:00' },
        friday: { isOpen: true, openTime: '08:00', closeTime: '18:00' },
        saturday: { isOpen: true, openTime: '09:00', closeTime: '15:00' },
        sunday: { isOpen: false },
      },
    },
  },
];

export const MOCK_UNITS: Unit[] = [
  {
    id: 1,
    propertyId: 1,
    unitNumber: 'A101',
    floor: 1,
    bedrooms: 2,
    bathrooms: 2,
    squareFootage: 1200,
    rentAmount: 2800,
    currency: 'USD',
    isOccupied: true,
    residentId: 1,
    moveInDate: new Date('2023-06-15T00:00:00Z'),
    amenities: ['Balcony', 'Dishwasher', 'In-Unit Laundry'],
    parkingSpaces: 1,
    balcony: true,
    furnished: false,
    petFriendly: true,
  },
  {
    id: 2,
    propertyId: 1,
    unitNumber: 'B205',
    floor: 2,
    bedrooms: 1,
    bathrooms: 1,
    squareFootage: 800,
    rentAmount: 2200,
    currency: 'USD',
    isOccupied: true,
    residentId: 2,
    moveInDate: new Date('2023-08-20T00:00:00Z'),
    amenities: ['Dishwasher', 'Hardwood Floors'],
    parkingSpaces: 1,
    balcony: false,
    furnished: false,
    petFriendly: true,
  },
  {
    id: 3,
    propertyId: 1,
    unitNumber: 'C301',
    floor: 3,
    bedrooms: 3,
    bathrooms: 2,
    squareFootage: 1500,
    rentAmount: 3500,
    currency: 'USD',
    isOccupied: false,
    moveOutDate: new Date('2024-02-01T00:00:00Z'),
    amenities: ['Balcony', 'Dishwasher', 'In-Unit Laundry', 'Walk-in Closet'],
    parkingSpaces: 2,
    balcony: true,
    furnished: false,
    petFriendly: true,
  },
];
